import {
  FixedDimension,
  PercentDimension,
  SingleNamedObject,
  NamedObjectContainer,
  SvgRootRectangle,
  getItemByField
} from "../../../../web-graphics";
import { DockBox } from "../webgl/dock-box";
import { Isoline } from "../webgl/isoline";
import { CentralSignalsBoxPanel } from "../webgl/central-signals-box-panel";
import { getChannelUnitText as _getChannelUnitText, getChannelUnitScale as _getChannelUnitScale } from "../../unit-info-getter";
import { LabelMarkGroup } from "./label-mark-group";

export class Plot extends SvgRootRectangle {
  /*
  borders: {
    location: {
      x: new ScalableDimension(0, 0),
      y: new ScalableDimension(0, 0)
    },
    htmlLocation: {
      x: new ScalableDimension(0, 0),
      y: new ScalableDimension(0, 0)
    },
    size: {
      width: new ScalableDimension(0, 1),
      height: new ScalableDimension(0, 1)
    },
    htmlSize: {
      width: new ScalableDimension(0, 1),
      height: new ScalableDimension(0, 1)
    },
    htmlParentSize: {
      width: 0,
      height: 0
    },
    // optional
    parentSize: {
      width: 0,
      height: 0
    },
    margin: {
      left: new ScalableDimension(0, 0),
      right: new ScalableDimension(0, 0),
      top: new ScalableDimension(0, 0),
      bottom: new ScalableDimension(0, 0)
    },
    padding: {
      left: new ScalableDimension(0, 0),
      right: new ScalableDimension(0, 0),
      top: new ScalableDimension(0, 0),
      bottom: new ScalableDimension(0, 0)
    }
  }
  settings: {
    signalsBox: {
      defaultCellSize_mm,onsole
      scaleX,
      scaleY,
      contentBorders: {},
      dataSources:[],
      filtersInfo:[],
      defaultFilters: []
    }
    ...
  }
  */
  constructor(settings) {
    super(Plot.setAdditionalSettings(settings));

    const gridSettings = Plot.findContentSettings(this.settings, "grid", "underCentral");
    const signalsSettings = Plot.findContentSettings(this.settings, "signals", "central");

    this.signalsList = []; // item: { signal: graphic object, sourceId: 0, channelId: 0, signalId: 0 }
    this.isolinesList = []; // item: { isoline: graphic object, sourceId: 0, channelId: 0, isolineId: 0 }
    this.signalsMap = new Map(); // key: sourceId, value: map{ key: channelId, value: map{ key: signalId, value: signalObject }
    this.signalLinkedObjects = []; // item: { signalIndex: signalsListIndex, object: graphic object }

    this.settings.signalsBox = {
      scaleX: signalsSettings.scaleX,
      scaleY: signalsSettings.scaleY,
      defaultCellSize_mm: gridSettings.defaultCellSize_mm,
      filters: this.settings.common?.data?.filters ?? [],
      selectedFiltersNames: signalsSettings.defaultFilters ?? [],
      location: {
        x: new FixedDimension(0),
        y: new FixedDimension(0)
      },
      size: {
        width: new PercentDimension(1),
        height: new PercentDimension(1)
      },
      contentBorders: this.settings.contentBorders,
      parent: {
        ref: this
      }
    };

    this.signalsBox = new DockBox(this.settings.signalsBox);

    this.dataSourcesContainer = new NamedObjectContainer("DataSources");
    const centralSignalsPanel = new CentralSignalsBoxPanel({
      location: {
        x: new FixedDimension(0),
        y: new FixedDimension(0)
      },
      size: {
        width: new PercentDimension(1),
        height: new PercentDimension(1)
      },
      parent: {
        ref: this.signalsBox.content.central
      },
      dataSourcesContainer: this.dataSourcesContainer,
      dataSourcesSettings: this.settings.dataSources
    });
    this.signalsBox.content.central.addItem(new SingleNamedObject("DataSourcesPanel", centralSignalsPanel));
    this.childItemsContainer.add(new SingleNamedObject("SignalsBox", this.signalsBox));
    this.childItemsContainer.add(this.dataSourcesContainer);
  }

  static setAdditionalSettings(settings) {
    settings.id = `Plot_${settings.title}_svgRoot`;
    return settings;
  }

  init() {
    this.createChannels();
  }

  getUnitSettings(unitId) {
    return getItemByField("id", unitId, this.settings.common.data.units);
  }

  /*
  place:
    "central",
    "aboveCentral",
    "underCentral",
    "bottom",
    "bottomCentral",
    "top",
    "topCentral",
    "left",
    "leftCentral",
    "right",
    "rightCentral"
  */
  addObjectToPanel(place, obj) {
    this.signalsBox.content[place].addItem(obj);
  }

  static findContentSettings(plotSettings, name, place) {
    const placeContents = plotSettings.plotContent[place];
    if (placeContents === undefined) {
      return undefined;
    }
    for (let contentIndex = 0; contentIndex < placeContents.length; contentIndex++) {
      if (placeContents[contentIndex].name == name) {
        return placeContents[contentIndex].settings;
      }
    }
    return undefined;
  }

  draw(gl, program) {
    this.childItemsContainer.recursiveExecute("draw", gl, program);
  }

  getSignal(sourceId, channelId, signalId) {
    let channelMap = this.signalsMap.get(sourceId);
    if (channelMap === undefined) {
      return undefined;
    }
    let channelInfo = channelMap.get(channelId);
    if (channelInfo === undefined) {
      return undefined;
    }
    return channelInfo.get(signalId);
  }

  linkObjectWithSignal(obj, sourceId, channelId, signalId, updateLocationFunc) {
    let signalObject = this.getSignal(sourceId, channelId, signalId);
    if (signalObject === undefined) {
      return;
    }
    this.signalLinkedObjects.push({
      signalObject: signalObject,
      object: obj,
      updateLocationFunc: updateLocationFunc
    });
  }

  // place: "leftCentral" | "rightCentral"
  addSignalLinkedObject(namedObject, place, sourceId, channelId, signalId, updateLocationFunc) {
    if ((place !== "leftCentral" && place !== "rightCentral") || this.signalsBox.content[place] === undefined) {
      return;
    }
    let signalObject = this.getSignal(sourceId, channelId, signalId);
    if (signalObject === undefined) {
      return;
    }
    this.signalLinkedObjects.push({
      signalObject: signalObject,
      object: namedObject.item,
      updateLocationFunc: updateLocationFunc
    });
    this.signalsBox.content[place].addItem(namedObject);
  }

  updateLinkedObjectsLocation() {
    for (let i = 0; i < this.signalLinkedObjects.length; i++) {
      let linkedObjectInfo = this.signalLinkedObjects[i];
      let signalY = linkedObjectInfo.signalObject.settings.locationInPixels.y;
      linkedObjectInfo.object.setLocation(
        linkedObjectInfo.object.settings.location.x,
        new FixedDimension(linkedObjectInfo.updateLocationFunc(signalY))
      );
      linkedObjectInfo.object.createBorders();
    }
  }

  createChannels() {
    this.signalsMap.clear();
    for (let dataSourceIndex = 0; dataSourceIndex < this.settings.dataSources.length; dataSourceIndex++) {
      const dataSourceSettings = this.settings.dataSources[dataSourceIndex];
      const channelsIndexMap = new Map();
      this.signalsMap.set(dataSourceSettings.id, channelsIndexMap);
      const dataSourceMappedInfo = {
        containerIndex: this.dataSourcesContainer.items.length,
        channelsIndices: new Map() // key: channelId, value: index in channelsContainer.items
      };
      const dataSourceContainer = new NamedObjectContainer(dataSourceSettings.id.toString());
      this.dataSourcesContainer.add(dataSourceContainer);
      const channelsContainer = new NamedObjectContainer("channels");
      dataSourceContainer.add(channelsContainer);
      for (let channelIndex = 0; channelIndex < dataSourceSettings.channels.length; channelIndex++) {
        const channelId = dataSourceSettings.channels[channelIndex].id;
        const signalsIndexMap = new Map();
        channelsIndexMap.set(channelId, signalsIndexMap);
        dataSourceMappedInfo.channelsIndices.set(channelId, dataSourceContainer.items.length);
        const channelContainer = new NamedObjectContainer(channelId.toString());
        channelsContainer.add(channelContainer);
        this.createSignals(dataSourceSettings.id, channelId, channelContainer, signalsIndexMap);
      }
      this.createMarks(dataSourceSettings, dataSourceContainer);
    }
  }

  createMarks(dataSourceSettings, dataSourceContainer) {
    const marksContainer = new NamedObjectContainer("marks");
    for (let markIndex = 0; markIndex < dataSourceSettings.marks.length; markIndex++) {
      const markSettings = dataSourceSettings.marks[markIndex];
      const parentPanel = this.signalsBox.content[markSettings.place];
      let x = markSettings.x.copy();
      if (markSettings.x_axis == "right") {
        if (markSettings.x instanceof PercentDimension) {
          x = new PercentDimension(1 - markSettings.x.calcValue(parentPanel.settings.clientSize.width) / parentPanel.settings.clientSize.width);
        } else {
          x = new FixedDimension(parentPanel.settings.clientSize.width - markSettings.x.calcValue(parentPanel.settings.clientSize.width));
        }
      }
      let y = markSettings.y.copy();
      if (markSettings.y_axis == "top") {
        if (markSettings.y instanceof PercentDimension) {
          y = new PercentDimension(1 - markSettings.y.calcValue(parentPanel.settings.clientSize.height) / parentPanel.settings.clientSize.height);
        } else {
          y = new FixedDimension(parentPanel.settings.clientSize.height - markSettings.y.calcValue(parentPanel.settings.clientSize.height));
        }
      }

      switch (markSettings.type) {
        case "text":
          {
            const markGroup = new LabelMarkGroup({
              location: {
                x: x,
                y: y
              },
              size: {
                width: markSettings.width,
                height: markSettings.height
              },
              parent: {
                ref: parentPanel
              },
              htmlObject: {
                parent: {
                  ref: this.settings.htmlObject.ref
                },
                graphicParent: this
              },
              signalDependent: markSettings.signalDependent ?? false,
              minXSpace: markSettings.min_x_space,
              minYSpace: markSettings.min_y_space,
              color: markSettings.color === undefined ? [0, 0, 0, 1] : [...markSettings.color]
            });
            const marksContainerItem = new SingleNamedObject(markSettings.id_mark.toString(), markGroup);
            marksContainer.add(marksContainerItem);
          }
          break;
      }
    }
    dataSourceContainer.add(marksContainer);
  }

  getSignalsAreaSize() {
    return this.signalsBox.content.central.settings.clientSize;
  }

  createSignals(dataSourceId, channelId, channelContainer, signalsIndexMap) {
    if (this.settings._signalsCreator === undefined) {
      return;
    }
    let isolinesContainer = new NamedObjectContainer("isolines");
    let signalsContainer = new NamedObjectContainer("signals");
    channelContainer.add(isolinesContainer);
    channelContainer.add(signalsContainer);

    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    channelContainer.hasVariableIsoline = channelSettings.hasVariableIsoline;
    const signalsAreaSize = this.getSignalsAreaSize();
    let channelY = channelSettings.y.calcValue(signalsAreaSize.height);
    let channelHeight = channelSettings.height.calcValue(signalsAreaSize.height);

    let currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    channelContainer.configuration = channelSettings.currConfiguration;

    if (this.beforeSignalObjectsCreate !== undefined) {
      this.beforeSignalObjectsCreate(channelSettings, channelContainer);
    }
    if (channelSettings.showIsolines) {
      for (let i = 0; i < currConfiguration.isolines.length; i++) {
        let isolineSettings = currConfiguration.isolines[i];
        let isolineY = channelY + isolineSettings.y.calcValue(channelHeight);
        //borders, plotLocation, plotSize, color, parent, canvas
        let isoline = new Isoline({
          location: {
            x: new FixedDimension(0),
            y: new FixedDimension(isolineY)
          },
          size: {
            width: new PercentDimension(1),
            height: new FixedDimension(1)
          },
          parent: {
            ref: this.signalsBox.content.central
          },
          plotLocation: { x: 0, y: 0 },
          plotSize: [signalsAreaSize.width, signalsAreaSize.height],
          color: isolineSettings.color
        });
        let isolineObject = new SingleNamedObject(isolineSettings.id.toString(), isoline);
        isolinesContainer.add(isolineObject);
      }
    }
    for (let signalIndex = 0; signalIndex < currConfiguration.signals.length; signalIndex++) {
      let signalSettings = currConfiguration.signals[signalIndex];
      let isolineSettings = getItemByField("id", signalSettings.id_isoline, currConfiguration.isolines);
      let isolineY = channelY + isolineSettings.y.calcValue(channelHeight);

      const signalObject = this.settings._signalsCreator.create({
        isolineY: isolineY,
        channelSettings: channelSettings,
        signalSettings: signalSettings,
        isolineSettings: isolineSettings,
        commonSettings: this.settings.common,
        plotSettings: this.settings,
        signalsBoxSettings: this.settings.signalsBox,
        plot: this,
        parent: this.signalsBox.content.central,
        ppcm: this.settings.ppcm
      });
      if (this.afterSignalObjectCreate !== undefined) {
        this.afterSignalObjectCreate(signalObject, dataSourceId, channelId, signalSettings.id);
      }

      const namedSignalObject = new SingleNamedObject(signalSettings.id.toString(), signalObject);
      signalsContainer.add(namedSignalObject);
      signalsIndexMap.set(signalSettings.id, signalObject);
    }
    if (this.afterSignalsCreate !== undefined) {
      this.afterSignalsCreate(dataSourceId, channelId);
    }
  }

  getSignalsWidthInMm() {
    const signalsAreaSize = this.getSignalsAreaSize();
    return signalsAreaSize.width / (this.settings.ppcm / 10);
  }

  getPlotPointCount(freq) {
    const signalsAreaSize = this.getSignalsAreaSize();
    let signalBufferWidth = signalsAreaSize.width / (this.settings.ppcm / 10); // in mm
    return Math.ceil((freq * signalBufferWidth) / this.settings.signalsBox.scaleX.currValue); // in points
  }

  getSignalsPointCount(dataSourceId, channelId) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);

    const channelScaleX =
      channelSettings._isSynchronizedScaleX ||
      channelSettings.scaleX === undefined ||
      channelSettings.scaleX.index === undefined ||
      channelSettings.scaleX.values === undefined
        ? this.settings.signalsBox.scaleX.currValue
        : channelSettings.scaleX.values[channelSettings.scaleX.index];
    const signalsAreaSize = this.getSignalsAreaSize();
    let signalBufferWidth = signalsAreaSize.width / (this.settings.ppcm / 10); // in mm
    return Math.ceil((channelSettings.freq * signalBufferWidth) / channelScaleX); // in points
  }

  setFilters(selectedFilterNames) {
    this.forEachSignal(signalObject => {
      signalObject.setFilters(selectedFilterNames);
    });
  }

  createBorders() {
    super.createBorders();
    this.updateLinkedObjectsLocation();
    this.onBufferSizeChanged?.();
  }

  setScaleX(newScaleX) {
    this.settings.signalsBox.scaleX.currValue = newScaleX;

    this.forEachSignal(signalObject => {
      signalObject.setScaleX(newScaleX);
    });

    this.afterChannelsDataClear?.();
    this.onBufferSizeChanged?.();
  }

  setScaleY(newScaleY) {
    this.settings.signalsBox.scaleY.currValue = newScaleY;

    this.forEachSignal((signalObject, sourceId, channelId) => {
      const channelSettings = this.getChannelSettings(sourceId, channelId);
      const unitScale = this.getChannelUnitScale(channelSettings);
      signalObject.setScaleY((newScaleY * unitScale) / this.settings.signalsBox.defaultCellSize_mm);
    });
  }

  getChannelUnitText(channelSettings) {
    return _getChannelUnitText(channelSettings, this.settings.common.data);
  }

  getChannelUnitScale(channelSettings) {
    return _getChannelUnitScale(channelSettings, this.settings.common.data);
  }

  initChannelScaleXSettings(dataSourceId, channelId) {
    this._initChannelScaleSettings("scaleX", dataSourceId, channelId);
  }

  initChannelScaleYSettings(dataSourceId, channelId) {
    this._initChannelScaleSettings("scaleY", dataSourceId, channelId);
  }

  initChannelFilterSettings(dataSourceId, channelId) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    if (channelSettings.filters === undefined) {
      channelSettings.filters = this.settings.signalsBox.selectedFiltersNames;
    }
  }

  setChannelScaleXSettings(dataSourceId, channelId, newScaleX) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    if (channelSettings.scaleX === undefined) {
      channelSettings.scaleX = {};
    }
    if (channelSettings.scaleX.values === undefined) {
      channelSettings.scaleX.values = [...this.settings.signalsBox.scaleX.values];
      channelSettings.scaleX._isUsedCommonValues = true;
    }
    channelSettings.scaleX._isUsedCommonIndex = false;
    channelSettings.scaleX.index = channelSettings.scaleX.values.findIndex(item => item == newScaleX);
  }

  setChannelScaleX(dataSourceId, channelId, newScaleX) {
    this.setChannelScaleXSettings(dataSourceId, channelId, newScaleX);
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.setScaleX(newScaleX);
    });
    this.onBufferSizeChanged?.();
  }

  setChannelScaleY(dataSourceId, channelId, newScaleY) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    if (channelSettings.scaleY === undefined) {
      channelSettings.scaleY = {};
    }
    if (channelSettings.scaleY.values === undefined) {
      channelSettings.scaleY.values = [...this.settings.signalsBox.scaleY.values];
      channelSettings.scaleY._isUsedCommonValues = true;
    }
    channelSettings.scaleY._isUsedCommonIndex = false;
    channelSettings.scaleY.index = channelSettings.scaleY.values.findIndex(item => item == newScaleY);

    const unitScale = this.getChannelUnitScale(channelSettings);
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.setScaleY((newScaleY * unitScale) / this.settings.signalsBox.defaultCellSize_mm);
    });
  }

  getChannelScaleY(dataSourceId, channelId) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    if (channelSettings.scaleY === undefined || channelSettings.scaleY.values === undefined || channelSettings.scaleY.index === undefined) {
      return undefined;
    }
    return channelSettings.scaleY.values[channelSettings.scaleY.index];
  }

  setChannelFilters(dataSourceId, channelId, selectedFilterNames) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    channelSettings.filters = [...selectedFilterNames];

    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.setFilters(selectedFilterNames);
    });
  }

  setFrequency(dataSourceId, channelId, freq) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    channelSettings.freq = freq;
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.setFrequency(freq);
    });
    this.onBufferSizeChanged?.();
  }

  getFirstSignalDataBufferSize() {
    let signalObject = this.getFirstSignalObject();
    if (signalObject === undefined) {
      return undefined;
    }
    return signalObject.getPointsCount();
  }

  getDataBufferSize(dataSourceId, channelId, signalId) {
    let signalObject = this.getSignalObject(dataSourceId, channelId, signalId);
    if (signalObject === undefined) {
      return undefined;
    }
    return signalObject.getPointsCount();
  }

  getMarkObjectGroup(dataSourceId, markId) {
    const dataSourceContainer = this.dataSourcesContainer.findItem(dataSourceId.toString());
    if (dataSourceContainer === undefined) {
      return undefined;
    }
    const marksContainer = dataSourceContainer.findItem("marks");
    if (marksContainer === undefined) {
      return undefined;
    }
    return marksContainer.findItem(markId.toString());
  }

  getSignalObject(dataSourceId, channelId, signalId) {
    let channelContainer = this.getChannelContainer(dataSourceId, channelId);
    if (channelContainer === undefined) {
      return undefined;
    }
    let signalsContainer = channelContainer.findItem("signals");
    if (signalsContainer === undefined) {
      return undefined;
    }
    let signalItem = signalsContainer.findItem(signalId.toString());
    return signalItem?.item;
  }

  getFirstChannelSignalObject(sourceId, channelId) {
    let channelSettings = this.getChannelSettings(sourceId, channelId);
    if (channelSettings === undefined) {
      return undefined;
    }
    let currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    if (currConfiguration.signals.length == 0) {
      return undefined;
    }
    let signalSettings = currConfiguration.signals[0];
    return this.getSignalObject(sourceId, channelId, signalSettings.id);
  }

  getFirstSignalObject() {
    if (this.settings.dataSources.length == 0) {
      return undefined;
    }
    let dataSourceSettings = this.settings.dataSources[0];
    if (dataSourceSettings.channels.length == 0) {
      return undefined;
    }
    let channelSettings = dataSourceSettings.channels[0];
    let currConfiguration = getItemByField("name", channelSettings.currConfiguration, channelSettings.configurations);
    if (currConfiguration.signals.length == 0) {
      return undefined;
    }
    let signalSettings = currConfiguration.signals[0];
    return this.getSignalObject(dataSourceSettings.id, channelSettings.id, signalSettings.id);
  }

  addMark(dataSourceId, markId, data) {
    const markObjectGroupItem = this.getMarkObjectGroup(dataSourceId, markId);
    if (markObjectGroupItem === undefined) {
      return;
    }
    const markObjectGroup = markObjectGroupItem.item;
    let position = 0;
    let currPointCounter = 0;
    let signalBufferLength = 0;
    const signalObject = this.getFirstSignalObject();
    if (signalObject !== undefined) {
      if (markObjectGroup.settings.signalDependent) {
        position = signalObject.getCurrPointX() ?? 0;
      }
      currPointCounter = signalObject.getCurrPointIndex() ?? 0;
      signalBufferLength = signalObject.getPointsCount();
    }
    markObjectGroup.addMark(data, {
      x: position,
      currPointIndex: currPointCounter,
      blockSize: 0,
      signalBufferLength: signalBufferLength
    });
  }

  clearSignalDependentMarks() {
    this.dataSourcesContainer.forEachChild(dataSourceContainer => {
      const marksContainer = dataSourceContainer.findItem("marks");
      marksContainer.forEachChild(markObjectGroupItem => {
        if (markObjectGroupItem.item.settings.signalDependent) {
          markObjectGroupItem.item.clear();
        }
      });
    });
  }

  tryRemoveMarks(currPointCounter, blockSize, signalBufferLength) {
    this.dataSourcesContainer.forEachChild(dataSourceContainer => {
      const marksContainer = dataSourceContainer.findItem("marks");
      marksContainer.forEachChild(markObjectGroupItem => {
        markObjectGroupItem.item.tryRemoveMarks(currPointCounter, blockSize, signalBufferLength);
      });
    });
  }

  // points in user units (e.g. mV) or in pixels (if valueConverter is not set)
  addPoints(dataSourceId, channelId, signalId, points) {
    const signalObject = this.getSignalObject(dataSourceId, channelId, signalId);
    if (signalObject !== undefined) {
      signalObject.addPoints(points);
      if (this.afterAddPointsToSignal !== undefined) {
        this.afterAddPointsToSignal(dataSourceId, channelId, signalId);
      }
    }
  }

  getFirstSignalCurrPointX() {
    const signalObject = this.getFirstSignalObject();
    if (signalObject === undefined) {
      return undefined;
    }
    return signalObject.getCurrPointX();
  }

  getSignalCurrPointX(dataSourceId, channelId, signalId) {
    const signalObject = this.getSignalObject(dataSourceId, channelId, signalId);
    if (signalObject === undefined) {
      return undefined;
    }
    return signalObject.getCurrPointX();
  }

  clearChannel(dataSourceId, channelId) {
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.resetBuffer();
    });
    if (this.afterChannelClear !== undefined) {
      this.afterChannelClear(dataSourceId, channelId);
    }
  }

  clearChannelData(dataSourceId, channelId) {
    this.forEachChannelSignal(dataSourceId, channelId, signalObject => {
      signalObject.resetBuffer();
    });
    if (this.afterChannelDataClear !== undefined) {
      this.afterChannelDataClear(dataSourceId, channelId);
    }
  }

  // newSettings: ChannelConfigurationPlotSettings
  updateCurrConfiguration(dataSourceId, channelId, newSettings) {
    const channelContainer = this._clearChannelContainer(dataSourceId, channelId);
    // add new signals
    const channelSettings = this.getChannelSettings(dataSourceId, channelId);
    for (let configIndex = 0; configIndex < channelSettings.configurations.length; configIndex++) {
      if (channelSettings.configurations[configIndex].name == channelSettings.currConfiguration) {
        channelSettings.configurations[configIndex] = newSettings.copy();
        break;
      }
    }
    this._afterChannelSettingsChanged(dataSourceId, channelId, channelContainer);
  }

  setDefaultConfiguration(dataSourceId, channelId) {
    const channelSettings = this.getChannelSettings(dataSourceId, channelId);
    this.setConfiguration(dataSourceId, channelId, channelSettings.defaultConfiguration);
  }

  setConfiguration(dataSourceId, channelId, configName) {
    const channelContainer = this._clearChannelContainer(dataSourceId, channelId);
    const channelSettings = this.getChannelSettings(dataSourceId, channelId);
    channelSettings.currConfiguration = configName;
    channelContainer.configuration = channelSettings.currConfiguration;
    this._afterChannelSettingsChanged(dataSourceId, channelId, channelContainer);
  }

  getChannelSettings(dataSourceId, channelId) {
    let dataSourceSettings = getItemByField("id", dataSourceId, this.settings.dataSources);
    return getItemByField("id", channelId, dataSourceSettings.channels);
  }

  forEachSignal(action) {
    this.dataSourcesContainer.forEachChild(dataSourceContainer => {
      const sourceId = parseInt(dataSourceContainer.name);
      let channelsContainer = dataSourceContainer.findItem("channels");
      if (channelsContainer !== undefined) {
        channelsContainer.forEachChild(channelContainer => {
          const channelId = parseInt(channelContainer.name);
          let signalsContainer = channelContainer.findItem("signals");
          if (signalsContainer !== undefined) {
            signalsContainer.forEachChild(signalObject => {
              const signalId = parseInt(signalObject.name);
              action(signalObject.item, sourceId, channelId, signalId);
            });
          }
        });
      }
    });
  }

  forEachChannelSignal(dataSourceId, channelId, action) {
    let channelContainer = this.getChannelContainer(dataSourceId, channelId);
    if (channelContainer === undefined) {
      return;
    }
    let signalsContainer = channelContainer.findItem("signals");
    if (signalsContainer === undefined) {
      return;
    }
    signalsContainer.forEach(action);
  }

  getChannelContainer(dataSourceId, channelId) {
    let dataSourceContainer = this.dataSourcesContainer.findItem(dataSourceId.toString());
    if (dataSourceContainer === undefined) {
      return undefined;
    }
    let channelsContainer = dataSourceContainer.findItem("channels");
    if (channelsContainer === undefined) {
      return undefined;
    }
    return channelsContainer.findItem(channelId.toString());
  }

  // fieldName = scaleX/scaleY
  _initChannelScaleSettings(fieldName, dataSourceId, channelId) {
    let channelSettings = this.getChannelSettings(dataSourceId, channelId);
    if (channelSettings[fieldName] === undefined) {
      channelSettings[fieldName] = {};
    }
    if (channelSettings[fieldName].values === undefined) {
      if (this.settings.signalsBox[fieldName].values !== undefined) {
        channelSettings[fieldName].values = [...this.settings.signalsBox[fieldName].values];
      } else {
        channelSettings[fieldName].values = [...this.settings.signalsBox[fieldName].defaultValues];
      }
      channelSettings[fieldName].index = channelSettings[fieldName].values.findIndex(
        item => item == this.settings.signalsBox[fieldName].currValue
      );
    }
    if (channelSettings[fieldName].index === undefined && channelSettings[fieldName].values.length > 0) {
      channelSettings[fieldName].index = 0;
    }
  }

  _clearChannelContainer(dataSourceId, channelId) {
    let channelContainer = this.getChannelContainer(dataSourceId, channelId);
    channelContainer.forEach(item => {
      item.removeObjectFromDom?.();
    });
    channelContainer.clear();
    if (this.afterChannelClear !== undefined) {
      this.afterChannelClear(dataSourceId, channelId);
    }
    return channelContainer;
  }

  _afterChannelSettingsChanged(dataSourceId, channelId, channelContainer) {
    if (this.afterChannelDataUpdated !== undefined) {
      this.afterChannelDataUpdated(dataSourceId, channelId);
    }
    let channelsIndexMap = this.signalsMap.get(dataSourceId);
    let signalsIndexMap = channelsIndexMap.get(channelId);
    signalsIndexMap.clear();
    this.createSignals(dataSourceId, channelId, channelContainer, signalsIndexMap);
  }
}
